import { Type, type Static } from "@sinclair/typebox";
import {
  DOCUMENT_VIEW_RESULT_MAX_LIMIT,
  DocumentTimestampSchema
} from "../document-organization.ts";
import { DocumentTagSchema } from "../document-tags.ts";
import { UuidSchema } from "../identifiers.ts";

export const DOCUMENT_VIEW_SCHEMA_VERSION = "2.0" as const;

export const DocumentViewTypeSchema = Type.Union([
  Type.Literal("recent_documents"),
  Type.Literal("timeline")
]);
export type DocumentViewTypeV2 = Static<typeof DocumentViewTypeSchema>;

export const DocumentViewRecordProperties = {
  schemaVersion: Type.Literal(DOCUMENT_VIEW_SCHEMA_VERSION),
  viewId: UuidSchema,
  collectionId: UuidSchema,
  name: Type.String({ minLength: 1, maxLength: 256 }),
  isDefault: Type.Boolean(),
  createdByPrincipalId: UuidSchema,
  revision: Type.Integer({ minimum: 0 }),
  createdAt: DocumentTimestampSchema,
  updatedAt: DocumentTimestampSchema
};

export const DocumentViewCreateProperties = {
  name: Type.String({ minLength: 1, maxLength: 256 }),
  makeDefault: Type.Optional(Type.Boolean())
};

export const DocumentViewUpdateProperties = {
  expectedRevision: Type.Integer({ minimum: 0 }),
  name: Type.String({ minLength: 1, maxLength: 256 }),
  makeDefault: Type.Optional(Type.Boolean())
};

export const DocumentViewResultDocumentSchema = Type.Object(
  {
    documentId: UuidSchema,
    fileSpaceId: UuidSchema,
    name: Type.String({ minLength: 1, maxLength: 512 }),
    path: Type.String({ minLength: 1, maxLength: 1024 }),
    mediaType: Type.String({ minLength: 1, maxLength: 256 }),
    lifecycleStatus: Type.Union([
      Type.Literal("active"),
      Type.Literal("trashed"),
      Type.Literal("missing"),
      Type.Literal("archived")
    ]),
    representationStatus: Type.Union([
      Type.Literal("absent"),
      Type.Literal("current"),
      Type.Literal("stale")
    ]),
    tags: Type.Array(DocumentTagSchema, { maxItems: 64 }),
    createdAt: DocumentTimestampSchema,
    updatedAt: DocumentTimestampSchema,
    diagnostics: Type.Array(
      Type.Object(
        {
          key: Type.String({ minLength: 1, maxLength: 64 }),
          code: Type.Literal("missing")
        },
        { additionalProperties: false }
      ),
      { maxItems: 32 }
    )
  },
  { additionalProperties: false }
);
export type DocumentViewResultDocumentV2 = Static<typeof DocumentViewResultDocumentSchema>;

export const DocumentViewCursorSchema = Type.Union([
  Type.String({ minLength: 1, maxLength: 2048 }),
  Type.Null()
]);

export const PageDocumentViewExecutionInputSchema = Type.Object(
  {
    operation: Type.Literal("page"),
    cursor: Type.Optional(Type.String({ minLength: 1, maxLength: 2048 })),
    limit: Type.Optional(Type.Integer({ minimum: 1, maximum: DOCUMENT_VIEW_RESULT_MAX_LIMIT }))
  },
  { additionalProperties: false }
);
export type PageDocumentViewExecutionInputV2 = Static<typeof PageDocumentViewExecutionInputSchema>;
